import { isObject } from 'lodash';

import { SnPointLike, SnPoint } from './point';

export class SnBounds {
  private _topLeft: SnPoint;
  private _bottomRight: SnPoint;

  public static convert(boundsLike: BoundsLike): SnBounds {
    if (boundsLike instanceof SnBounds) {
      return boundsLike;
    }

    if (Array.isArray(boundsLike) && boundsLike.length === 2) {
      return new SnBounds(boundsLike[0], boundsLike[1]);
    }

    if (Array.isArray(boundsLike) && boundsLike.length === 4) {
      const tl: SnPointLike = [boundsLike[0], boundsLike[1]];
      const br: SnPointLike = [boundsLike[2], boundsLike[3]];

      return new SnBounds(tl, br);
    }

    if (!Array.isArray(boundsLike) && isObject(boundsLike)) {
      const tl: SnPointLike = [boundsLike.left, boundsLike.top];
      const br: SnPointLike = [boundsLike.right, boundsLike.bottom];

      return new SnBounds(tl, br);
    }

    throw new Error(`
      BoundsLike argument must be specified as a Bounds instance,
      an array of [<PointLike>, <PointLike>]
    `);
  }

  constructor(tl: SnPointLike, br: SnPointLike) {
    this.extend(tl);
    this.extend(br);
  }

  public getBottomLeft(): SnPoint {
    return new SnPoint(this._topLeft.x, this._bottomRight.y);
  }

  public getTopRight(): SnPoint {
    return new SnPoint(this._bottomRight.x, this._topLeft.y);
  }

  public getTopLeft(): SnPoint {
    return this._topLeft;
  }

  public getBottomRight(): SnPoint {
    return this._bottomRight;
  }

  public getTop(): number {
    return this._topLeft.y;
  }

  public getRight(): number {
    return this._bottomRight.x;
  }

  public getBottom(): number {
    return this._bottomRight.y;
  }

  public getLeft(): number {
    return this._topLeft.x;
  }

  public getCenter(): SnPoint {
    const x = (this._topLeft.x + this._bottomRight.x) / 2;
    const y = (this._topLeft.y + this._bottomRight.y) / 2;

    return new SnPoint(x, y);
  }

  public getSize(): SnPoint {
    return this._bottomRight.subtract(this._topLeft);
  }

  public setTopLeft(pointLike: SnPointLike): this {
    this._topLeft = SnPoint.convert(pointLike);
    return this;
  }

  public setBottomRight(pointLike: SnPointLike): this {
    this._bottomRight = SnPoint.convert(pointLike);
    return this;
  }

  public extend(pointLike: SnPointLike): this {
    const point = SnPoint.convert(pointLike);

    if (!this._topLeft && !this._bottomRight) {
      this._topLeft = point.clone();
      this._bottomRight = point.clone();
    } else {
      this._topLeft.x = Math.min(point.x, this._topLeft.x);
      this._bottomRight.x = Math.max(point.x, this._bottomRight.x);
      this._topLeft.y = Math.min(point.y, this._topLeft.y);
      this._bottomRight.y = Math.max(point.y, this._bottomRight.y);
    }

    return this;
  }

  public toArray(): [[number, number], [number, number]] {
    return [this._topLeft.toArray(), this._bottomRight.toArray()];
  }

  public toString(): string {
    return `(${this._topLeft.toString()}, ${this._bottomRight.toString()})`;
  }

  public toJSON(): IBoundsLiteral {
    return {
      top: this.getTop(),
      right: this.getRight(),
      bottom: this.getBottom(),
      left: this.getLeft(),
    };
  }
}

export interface IBoundsLiteral {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type BoundsLike =
  | SnBounds
  | IBoundsLiteral
  | [SnPointLike, SnPointLike]
  | [number, number, number, number];
